import { useEffect, useCallback, useRef, useMemo } from 'react';
import { Toaster, useToastController, Toast, ToastBody, ToastTitle } from '@fluentui/react-components';
import { AlertUrgentRegular, AlertRegular, CheckmarkCircleRegular, InfoRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useSignalR } from 'contexts/SignalRContext';
import { useSettings } from 'hooks/useSettings';
import type { SignalRMessage } from 'types';
import { SignalREventType, IncidentSeverity } from 'types';

const NOTIFICATION_EVENT_TYPES = new Set<SignalREventType>([
  SignalREventType.IncidentCreated,
  SignalREventType.IncidentNotification,
  SignalREventType.SlaWarning,
  SignalREventType.SlaBreached,
]);

interface NotificationManagerProps {
  children: React.ReactNode;
}

/**
 * Manages notification dispatch based on SignalR events.
 * Shows toast notifications and plays audio alerts when incidents are created
 * or SLA deadlines are approaching/breached.
 */
export const NotificationManager: React.FC<NotificationManagerProps> = ({ children }) => {
  const { t } = useTranslation();
  const { subscribe } = useSignalR();
  const { settings } = useSettings();
  const { dispatchToast } = useToastController('notification-toaster');
  const processedIds = useRef(new Set<string>());

  // Get sound configs from settings - memoized to prevent useCallback dependency changes
  const soundConfigs = useMemo(() => settings?.soundConfigs ?? [], [settings?.soundConfigs]);

  const showNotification = useCallback((message: SignalRMessage) => {
    const incident = message.incident;
    if (!incident) return;

    const eventType = message.eventType;
    
    // Determine notification reason
    let reason: 'Created' | 'SlaWarning' | 'SlaBreached' = 'Created';
    let titleKey = 'statusPanel.notification.created';
    
    if (eventType === SignalREventType.SlaWarning) {
      reason = 'SlaWarning';
      titleKey = 'statusPanel.notification.slaWarning';
    } else if (eventType === SignalREventType.SlaBreached) {
      reason = 'SlaBreached';
      titleKey = 'statusPanel.notification.slaBreached';
    }

    const severity = incident.severity;

    // Play audio alert if enabled for this severity
    const config = soundConfigs.find((c) => c.severity === severity);
    if (config?.enabled && config?.soundFileData) {
      try {
        const audio = new Audio(config.soundFileData);
        audio.play().catch((err) => console.error('Failed to play audio:', err));
      } catch (err) {
        console.error('Failed to play audio alert:', err);
      }
    }

    // Determine icon based on severity
    const getIcon = () => {
      switch (severity) {
        case IncidentSeverity.High:
          return <AlertUrgentRegular />;
        case IncidentSeverity.Medium:
          return <AlertRegular />;
        case IncidentSeverity.Low:
          return <CheckmarkCircleRegular />;
        default:
          return <InfoRegular />;
      }
    };

    // Determine toast appearance based on severity
    const getAppearance = (): 'success' | 'warning' | 'error' => {
      switch (severity) {
        case IncidentSeverity.High:
          return 'error';
        case IncidentSeverity.Medium:
          return 'warning';
        case IncidentSeverity.Low:
        default:
          return 'success';
      }
    };

    // Get severity label
    const getSeverityLabel = () => {
      switch (severity) {
        case IncidentSeverity.High:
          return 'High';
        case IncidentSeverity.Medium:
          return 'Medium';
        case IncidentSeverity.Low:
          return 'Low';
        default:
          return 'Informational';
      }
    };

    dispatchToast(
      <Toast>
        <ToastTitle>
          {getIcon()} {t(titleKey, { defaultValue: reason === 'Created' ? 'New Incident' : reason })}
        </ToastTitle>
        <ToastBody subtitle={`Severity: ${getSeverityLabel()}`}>
          {incident.title}
        </ToastBody>
      </Toast>,
      { intent: getAppearance(), timeout: 7000 }
    );
  }, [soundConfigs, dispatchToast, t]);

  const handleMessage = useCallback((message: SignalRMessage) => {
    // Only process notification events
    if (!NOTIFICATION_EVENT_TYPES.has(message.eventType)) {
      return;
    }

    // Deduplicate - prevent showing same notification multiple times
    const notificationKey = `${message.eventType}-${message.incidentId}`;
    if (processedIds.current.has(notificationKey)) {
      return;
    }
    processedIds.current.add(notificationKey);

    // Clean up old notification keys after 60 seconds
    setTimeout(() => {
      processedIds.current.delete(notificationKey);
    }, 60000);

    showNotification(message);
  }, [showNotification]);

  useEffect(() => {
    const unsubscribe = subscribe(handleMessage);
    return () => {
      unsubscribe();
    };
  }, [subscribe, handleMessage]);

  return (
    <>
      <Toaster toasterId="notification-toaster" position="top-end" />
      {children}
    </>
  );
};
